import {
  IRegisterTan,
  IFeigCustomer,
  IAppPermission,
  IAppCustomerRole,
} from '@license-manager/types';
import {
  ICustomer,
  IAppUser,
  EManagementRoles,
  IManagementUser,
  ILegalText,
} from '@license-manager/types';
import {
  PureAbility,
  AbilityBuilder,
  createMongoAbility,
  buildMongoQueryMatcher,
} from '@casl/ability';
import { $not, not } from '@ucast/mongo2js';

const conditionsMatcher = buildMongoQueryMatcher({ $not }, { not });

export function getRolesForUser(user: IManagementUser): EManagementRoles[] {
  if (user.role == EManagementRoles.CUSTOMER_USER) {
    return [EManagementRoles.CUSTOMER_USER];
  } else if (user.role == EManagementRoles.CUSTOMER_ADMIN) {
    return [EManagementRoles.CUSTOMER_USER, EManagementRoles.CUSTOMER_ADMIN];
  } else if (user.role == EManagementRoles.FEIG_ADMIN) {
    return [
      EManagementRoles.CUSTOMER_USER,
      EManagementRoles.CUSTOMER_ADMIN,
      EManagementRoles.FEIG_ADMIN,
    ];
  }
}

export enum Action {
  Manage = 'manage',
  Create = 'create',
  Read = 'read',
  Update = 'update',
  Delete = 'delete',
}

export type Subjects =
  | 'all'
  | 'AppUser'
  | 'ManagementUser'
  | 'Customer'
  | 'LegalText'
  | 'RegisterTan'
  | 'FeigCustomer'
  | 'AppPermission'
  | 'AppPermissionDomain'
  | 'AppCustomerRole'
  | IRegisterTan
  | IAppUser
  | IManagementUser
  | ICustomer
  | ILegalText
  | IFeigCustomer
  | IAppPermission
  | IAppCustomerRole;

export type AppAbility = PureAbility<[Action, Subjects]>;

export function createForUser(user: IManagementUser) {
  const { can, cannot, build } = new AbilityBuilder<AppAbility>(createMongoAbility);

  // no one -> DEFAULT, ALLOW LATER
  cannot(Action.Manage, 'Customer', ['appcolorscheme.stylesheet']);

  if (user.role === EManagementRoles.FEIG_ADMIN) {
    can(Action.Manage, 'all'); // read-write access to everything
    can(Action.Manage, 'Customer', ['appcolorscheme.stylesheet']);

  } else if (user.role === EManagementRoles.CUSTOMER_ADMIN) {
    // can manage own licenses
    // customeraccess.custumer != user.customer.id
    can(Action.Manage, 'AppUser', {
      customeraccess: {
        $not: {
          $elemMatch: {
            'customer.id': { $nin: [(user.customer as ICustomer).id] },
          },
        },
      },
    });

    can(Action.Manage, 'AppUser', {
      customeraccess: {
        $not: {
          $elemMatch: { customer: { $nin: [(user.customer as ICustomer).id] } },
        },
      },
    });


    // Customer can manage their tans (marked by customer.id in Tan-Obj)
    can(Action.Manage, 'RegisterTan', {
      'customer.id': (user.customer as ICustomer).id,
    }); // check by id

    can(Action.Manage, 'RegisterTan', {
      customer: (user.customer as ICustomer).id,
    }); // check by customer

    // can manage own ManagementUsers
    can(Action.Manage, 'ManagementUser', {
      'customer.id': (user.customer as ICustomer).id,
    });

    can(Action.Manage, 'ManagementUser', {
      customer: (user.customer as ICustomer).id,
    });

    /**
     * FEIGCustomers -> only ids from user.customer.feigCustomerNumbers
     */
    can(Action.Read, 'FeigCustomer', {
      id: {
        $in: (
          (user.customer as ICustomer)?.feigCustomerNumbers ?? []
        ).map((id) => Number(id)),
      },
    });

    /**
     * AppPermissionDomain
     */
    can(Action.Manage, 'AppPermissionDomain', {
      customer: (user.customer as ICustomer).id,
    });

    can(Action.Manage, 'AppPermissionDomain', {
      customerId: (user.customer as ICustomer).id,
    });

    can(Action.Manage, 'AppPermissionDomain', {
      'customer.id': (user.customer as ICustomer).id,
    });

    /**
     * AppPermission
     */
    can([Action.Manage, Action.Read], 'AppPermission', {
      'domain.customer': (user.customer as ICustomer).id,
    });

    can([Action.Manage, Action.Read], 'AppPermission', {
      'domain.customerId': (user.customer as ICustomer).id,
    });

    can([Action.Manage, Action.Read], 'AppPermission', {
      'domain.customer.id': (user.customer as ICustomer).id,
    });

    // can Update+ read own customer
    can([Action.Update, Action.Read], 'Customer', {
      id: (user.customer as ICustomer).id,
    });
    // Can manage own customer roles
    can(Action.Manage, 'AppCustomerRole', {
      customerId: (user.customer as ICustomer).id,
    })

    can(Action.Manage, 'AppCustomerRole', {
      'customer.id': (user.customer as ICustomer).id,
    })

    // Can read own customer and global roles
    can(Action.Read, 'AppCustomerRole', {
      $elemMatch: {
        customerId: { $in: [(user.customer as ICustomer).id, null] }
      }
    })

    can(Action.Read, 'AppCustomerRole', {
      $elemMatch: {
        'customer.id': { $in: [(user.customer as ICustomer).id, null] }
      }
    })

  } else if (user.role === EManagementRoles.CUSTOMER_USER) {
    // can manage own licenses

    // customeraccess.custumer != user.customer.id
    can(Action.Manage, 'AppUser', {
      customeraccess: {
        $not: {
          $elemMatch: {
            'customer.id': { $nin: [(user.customer as ICustomer).id] },
          },
        },
      },
    });

    can(Action.Manage, 'AppUser', {
      customeraccess: {
        $not: {
          $elemMatch: { customer: { $nin: [(user.customer as ICustomer).id] } },
        },
      },
    });


    can(Action.Manage, 'RegisterTan', {
      'customer.id': (user.customer as ICustomer).id,
    }); // check by id

    can(Action.Manage, 'RegisterTan', {
      customer: (user.customer as ICustomer).id,
    }); // check by customer

    // can update, read and delete own user data
    can([Action.Update, Action.Read, Action.Delete], 'ManagementUser', {
      id: user.id,
    });
    // can read all users of this customer
    can(Action.Read, 'ManagementUser', {
      customer: (user.customer as ICustomer).id,
    });
    can(Action.Read, 'ManagementUser', {
      'customer.id': (user.customer as ICustomer).id,
    });

    /**
     * AppPermissionDomain
     */
    can(Action.Read, 'AppPermissionDomain', {
      customer: (user.customer as ICustomer).id,
    });

    can(Action.Read, 'AppPermissionDomain', {
      customerId: (user.customer as ICustomer).id,
    });

    can(Action.Read, 'AppPermissionDomain', {
      'customer.id': (user.customer as ICustomer).id,
    });

    can(Action.Read, 'AppPermission', {
      'domain.customer': (user.customer as ICustomer).id,
    });

    can(Action.Read, 'AppPermission', {
      'domain.customerId': (user.customer as ICustomer).id,
    });

    can(Action.Read, 'AppPermission', {
      'domain.customer.id': (user.customer as ICustomer).id,
    });

    // read own customer data
    can(Action.Read, 'Customer', { id: (user.customer as ICustomer).id });

    // Can read own customer and global roles
    can(Action.Read, 'AppCustomerRole', {
      $elemMatch: {
        customerId: { $in: [(user.customer as ICustomer).id, null] }
      }
    })

    can(Action.Read, 'AppCustomerRole', {
      $elemMatch: {
        'customer.id': { $in: [(user.customer as ICustomer).id, null] }
      }
    })


  }

  return build({ conditionsMatcher });
}
